/**
 * Map a mock domain key (e.g. 'networking') to a REAL topic name that the
 * backend stores on questions (e.g. "Networking" / "Virtual Networking").
 *
 * The Study Mix screen is built on mocked Skill-IQ domains (`planData.ts`),
 * but session creation hits the live API, which exact-matches
 * `question.topic = :topic`. Passing the raw key matches zero questions
 * (QA-001), so every launch must resolve the key against the live topic list
 * from `useTopics()` first.
 *
 * Strategy (in order):
 *  1. Exact case-insensitive match of a known alias against a live topic.
 *  2. Substring match of any alias keyword within a live topic name.
 *  3. Fallback: no topic filter (undefined) → an all-topics session that
 *     still loads questions, rather than an empty one.
 */

/** Keyword aliases per domain key, most-specific first. */
export const TOPIC_ALIASES: Record<string, string[]> = {
  identity: ['identity & governance', 'identity', 'governance', 'rbac'],
  storage: ['storage'],
  compute: ['compute'],
  networking: ['virtual networking', 'networking', 'network', 'vnet'],
  monitor: ['monitoring & backup', 'monitor & maintain', 'monitoring', 'monitor', 'backup', 'maintain'],
};

function norm(s: string): string {
  return s.trim().toLowerCase();
}

/** True when a live topic name belongs to a domain key (via its aliases). */
export function topicMatchesDomain(topic: string, key: string): boolean {
  const n = norm(topic);
  const aliases = TOPIC_ALIASES[key] ?? [norm(key)];
  return aliases.some((a) => n === a || n.includes(a));
}

/**
 * Resolve a mock domain key to a live topic name, or `undefined` when no
 * confident match exists (caller should then launch without a topic filter).
 */
export function resolveTopicName(key: string, liveTopics: string[]): string | undefined {
  const aliases = TOPIC_ALIASES[key] ?? [norm(key)];
  const normalized = liveTopics.map((t) => ({ raw: t, norm: norm(t) }));

  // 1 — exact alias === topic
  for (const alias of aliases) {
    const exact = normalized.find((t) => t.norm === alias);
    if (exact) return exact.raw;
  }
  // 2 — alias keyword contained in a topic name
  for (const alias of aliases) {
    const partial = normalized.find((t) => t.norm.includes(alias));
    if (partial) return partial.raw;
  }
  return undefined;
}
